import * as Dialog from "@radix-ui/react-dialog"
import TypedEventEmitter from "library/TypedEventEmitter"
import { fmobile, fresponsive, ftablet } from "library/fullyResponsive"
import { useCallback, useEffect, useRef, useState } from "react"
import styled, { css, keyframes } from "styled-components"
import colors from "styles/colors"
import counterMP4 from "videos/Search&Rescue.mp4"
import apexMP4 from "videos/TrueAutomation.mp4"
import radarMP4 from "videos/WildfirePrevention.mp4"
import { useVideoConfig } from "./Providers/VideoModalContext"

import Close from "./buttons/Close"

const events = new TypedEventEmitter<{
	sync: []
}>()

const videoSources = {
	apex: apexMP4,
	ranger: radarMP4,
	scout: counterMP4,
}

export const useVideoModal = (): [
	open: boolean,
	setOpen: (open: boolean) => void,
] => {
	const [value, setValue] = useState<boolean>(false)

	useEffect(() => {
		const onSync = () => {
			const newValue = new URLSearchParams(window.location.search).has(
				"videoOpen",
			)

			setValue(newValue)
		}

		onSync()

		events.addEventListener("sync", onSync)
		return () => {
			events.removeEventListener("sync", onSync)
		}
	}, [])

	const externalSetValue = useCallback((newValue: boolean) => {
		window.history.replaceState(
			{},
			"",
			`${window.location.pathname}${newValue ? "?videoOpen" : ""}`,
		)
		events.dispatchEvent("sync")
	}, [])

	return [value, externalSetValue]
}

export default function VideoModal() {
	const [open, setOpen] = useVideoModal()
	const { videoSource } = useVideoConfig()

	const videoRef = useRef<HTMLVideoElement>(null)
	const [videoToUse, setVideoToUse] = useState(videoSources[videoSource])

	useEffect(() => {
		setVideoToUse(videoSources[videoSource])
	}, [videoSource])

	return (
		<Dialog.Root
			open={open}
			onOpenChange={(newOpen) => {
				setOpen(newOpen)
			}}
		>
			<Overlay />
			<Content onOpenAutoFocus={(e) => e.preventDefault()}>
				<ContentInner>
					<Video
						className="video-wrapper-hardware"
						playsInline
						preload="metadata"
						autoPlay
						src={videoToUse}
						ref={videoRef}
					>
						<source src={videoToUse} type="video/mp4" />
					</Video>
					<Dialog.Close asChild>
						<StyledClose type="button" />
					</Dialog.Close>
				</ContentInner>
			</Content>
		</Dialog.Root>
	)
}

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`

const slideIn = keyframes`
  /* we need to start on screen to prevent any scroll-to-element from happening */
  0% {
    opacity: 0;
    translate: 0 0;
  }
  1% {
    opacity:  0;
    translate: 0 -100vh;
  }
  2% {
    opacity:  1;
    translate: 0 -100vh;
  }
  100% {
    opacity:  1;
    translate: 0 0
  }
`

const slideOut = keyframes`
  from {
    translate: 0 0
  }
  to {
    translate: 0 -100vh
  }
`

const Overlay = styled(Dialog.Overlay)`
	position: fixed;
	inset: 0;
	z-index: 101;
	background: rgb(0 0 0 / 64%);
	backdrop-filter: blur(12px);

	&[data-state="open"] {
		animation: ${fadeIn} 0.5s ease-out;
	}

	&[data-state="closed"] {
		animation: ${fadeOut} 0.5s ease-out;
	}
`

const Content = styled(Dialog.Content)`
	${fresponsive(css`
		position: fixed;
		z-index: 102;
		width: 1360px;
		height: 720px;
		left: calc(50% - calc(1360px / 2));
		top: calc(50% - calc(720px / 2));
		color: ${colors.white};
		border-radius: 24px;
		overflow: clip;
	`)}

	${ftablet(css`
		--height: min(90vh, 1286px);

		/* stylelint-disable plugin/no-unsupported-browser-features */
		@supports (height: min(90dvh, 1286px)) {
			--height: min(90dvh, 1286px);
		}

		width: 946px;
		height: var(--height);
		left: calc(50% - calc(946px / 2));
		top: calc(50% - calc(var(--height) / 2));
		border-radius: 24px;
	`)}

  ${fmobile(css`
		--height: min(90vh, 880px);

		@supports (height: min(90dvh, 880px)) {
			--height: min(90dvh, 880px);
		}

		width: 343px;
		height: var(--height);
		left: calc(50% - calc(343px / 2));
		top: calc(50% - calc(var(--height) / 2));
		gap: 93px;
	`)}

  &[data-state="open"] {
		animation: ${slideIn} 0.5s cubic-bezier(0.165, 0.84, 0.44, 1);
	}

	&[data-state="closed"] {
		animation: ${slideOut} 0.5s cubic-bezier(0.895, 0.03, 0.685, 0.22);
	}
`

const ContentInner = styled.div`
	width: 100%;
	height: 100%;

	&::-webkit-scrollbar {
		display: none;
	}

	${fresponsive(css`
		display: flex;
		justify-content: space-between;
	`)}

	${ftablet(css`
		flex-direction: column;
		align-items: center;
		justify-content: center;
	`)}

  ${fmobile(css`
		flex-direction: column;
		align-items: center;
		justify-content: center;
	`)}
`

const Video = styled.video`
	width: 100%;
	height: 100%;
	object-fit: cover;

	${fresponsive(css`
		border-radius: 24px;
	`)}

	${ftablet(css`
		width: 945px;
		height: auto;
	`)}

	${fmobile(css`
		width: 344px;
		height: auto;
	`)}
`

const StyledClose = styled(Close)`
	background: ${colors.grey04}20;
	backdrop-filter: blur(12px);
`
